import { ApiSource, Brand } from '../context/claims';
import { type NavigationAdapter } from '../../platform/navigation/NavigationAdapter';
import { type IframeAdapter } from '../../platform/iframe/IframeAdapter';
import { mixpanelService } from '../services/mixpanel';

export type RedirectReason = 'ACCEPTED' | 'REJECTED' | 'FAILED' | 'TIMEOUT' | 'ERROR';

const mobilePayErrorMessage = { message: 'unknown', code: 9 };

const mobilePayMessages = new Map<RedirectReason, { message: string; code: number }>([
  // e.g. user accepted and psp approved
  ['ACCEPTED', { message: 'completed', code: 0 }],

  // e.g. user cancelled payment
  ['REJECTED', { message: 'rejected', code: 1 }],

  // e.g. insufficient funds
  ['FAILED', { message: 'failed', code: 2 }],

  // e.g. payment expired pending either user approval or action from psp
  ['TIMEOUT', { message: 'expired', code: 3 }],

  // e.g. unknown redirect reason, should not happen
  ['ERROR', mobilePayErrorMessage],
]);

export function redirect(
  url: string | undefined,
  target: RedirectReason,
  brand: Brand,
  apiSource: ApiSource,
  isMobile: boolean,
  navigation: NavigationAdapter,
  iframe: IframeAdapter
) {
  mixpanelService.trackOutcome(target);

  const sdkEvent = target === 'ACCEPTED' ? 'complete' : 'cancel';
  iframe.postToParent({ event: sdkEvent, redirectUrl: url });

  if (brand === Brand.MOBILEPAY) {
    const { code, message } = mobilePayMessages.get(target) ?? mobilePayErrorMessage;

    // publish message for manual redirect in case of IFrame nesting
    iframe.postToParent(`mobilepay:rc=${code}&message=${message}`);
  }

  let queryParams = '';
  if (apiSource == ApiSource.MPAppPayments && isMobile) {
    queryParams = getQueryParamsForAppPayments(url!, target);
  }

  const maybeUrl = tryParseUrl(url + queryParams);
  if (maybeUrl) {
    navigation.assign(maybeUrl);
  } else {
    navigation.back();
  }
}

function getQueryParamsForAppPayments(url: string | undefined, reason: RedirectReason): string {
  const query = url?.includes('?') ? '&' : '?';
  if (reason === 'ACCEPTED') {
    return query + 'status=successful';
  }
  return query + 'status=rejected';
}

function tryParseUrl(maybeUrl: string | undefined): string | undefined {
  if (maybeUrl === undefined) {
    return undefined;
  }

  try {
    const url = new URL(maybeUrl);

    // allow all protocols but javascript, deepllinks could be used
    return url.protocol !== 'javascript:' ? url.toString() : undefined;
  } catch {
    return undefined;
  }
}
